import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export type CategoriaCookie = 'analisis' | 'terceros';

export interface PreferenciasCookies {
  analisis: boolean;
  terceros: boolean;
}

const NOMBRE_COOKIE_ESTADO = 'cookieControl';
const NOMBRE_COOKIE_PREFERENCIAS = 'cookieControlPrefs';
const DIAS_EXPIRACION_ESTADO = 30;
const DIAS_EXPIRACION_PREFERENCIAS = 365;

const COOKIES_NECESARIAS = ['PHPSESSID', NOMBRE_COOKIE_ESTADO, NOMBRE_COOKIE_PREFERENCIAS];
const PREFIJOS_COOKIES_ANALISIS = ['_ga'];
const PREFIJOS_COOKIES_TERCEROS = ['VISITOR_INFO1_LIVE', 'YSC', 'NID', '1P_JAR'];

const PREFERENCIAS_POR_DEFECTO: PreferenciasCookies = {
  analisis: false,
  terceros: false,
};

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly preferencias$ = new BehaviorSubject<PreferenciasCookies>(
    this.leerPreferenciasGuardadas(),
  );

  readonly preferencias = this.preferencias$.asObservable();

  private readonly solicitudApertura$ = new Subject<void>();
  readonly solicitudApertura = this.solicitudApertura$.asObservable();

  solicitarConfiguracion(): void {
    this.solicitudApertura$.next();
  }

  haRespondido(): boolean {
    return this.leerCookie(NOMBRE_COOKIE_ESTADO) !== null;
  }

  obtenerPreferencias(): PreferenciasCookies {
    return this.preferencias$.value;
  }

  tienePermiso(categoria: CategoriaCookie): boolean {
    return this.preferencias$.value[categoria] === true;
  }

  aceptarTodas(): void {
    this.guardar({ analisis: true, terceros: true });
  }

  rechazarNoEsenciales(): void {
    this.guardar({ analisis: false, terceros: false });
  }

  guardarPreferencias(preferencias: PreferenciasCookies): void {
    this.guardar(preferencias);
  }

  private guardar(preferencias: PreferenciasCookies): void {
    this.escribirCookie(NOMBRE_COOKIE_ESTADO, 'true', DIAS_EXPIRACION_ESTADO);
    this.escribirCookie(
      NOMBRE_COOKIE_PREFERENCIAS,
      JSON.stringify(preferencias),
      DIAS_EXPIRACION_PREFERENCIAS,
    );

    if (!preferencias.analisis) {
      this.borrarCookiesPorPrefijos(PREFIJOS_COOKIES_ANALISIS);
    }
    if (!preferencias.terceros) {
      this.borrarCookiesPorPrefijos(PREFIJOS_COOKIES_TERCEROS);
    }

    this.preferencias$.next(preferencias);
  }

  private borrarCookiesPorPrefijos(prefijos: string[]): void {
    document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter((nombre) => nombre.length > 0 && !COOKIES_NECESARIAS.includes(nombre))
      .filter((nombre) => prefijos.some((prefijo) => nombre.startsWith(prefijo)))
      .forEach((nombre) => this.borrarCookie(nombre));
  }

  private borrarCookie(nombre: string): void {
    const expira = 'expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/';

    // gtag.js guarda _ga/_ga_XXXX con un Domain explícito (para compartirla
    // entre subdominios, p. ej. panel.unifersa.net). Si al borrar no se
    // indica ese mismo dominio, el navegador no la sobrescribe: probamos
    // el host actual y sus dominios padre con y sin el punto inicial.
    document.cookie = `${nombre}=;${expira}`;

    const partesHost = location.hostname.split('.');
    for (let i = 0; i < partesHost.length - 1; i++) {
      const dominio = partesHost.slice(i).join('.');
      document.cookie = `${nombre}=;${expira};domain=${dominio}`;
      document.cookie = `${nombre}=;${expira};domain=.${dominio}`;
    }
  }

  private leerPreferenciasGuardadas(): PreferenciasCookies {
    const crudo = this.leerCookie(NOMBRE_COOKIE_PREFERENCIAS);
    if (!crudo) {
      return PREFERENCIAS_POR_DEFECTO;
    }

    try {
      return { ...PREFERENCIAS_POR_DEFECTO, ...JSON.parse(crudo) };
    } catch {
      return PREFERENCIAS_POR_DEFECTO;
    }
  }

  private leerCookie(nombre: string): string | null {
    const match = document.cookie.match(
      new RegExp('(^| )' + nombre + '=([^;]+)'),
    );
    return match ? decodeURIComponent(match[2]) : null;
  }

  private escribirCookie(nombre: string, valor: string, dias: number): void {
    const fecha = new Date();
    fecha.setTime(fecha.getTime() + dias * 24 * 60 * 60 * 1000);
    document.cookie = `${nombre}=${encodeURIComponent(valor)};expires=${fecha.toUTCString()};path=/`;
  }
}
